import React, {useCallback, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ProjectCreationContext, ProjectCreationContextProps} from "./project-creation-context.ts";
import UseImmerReducer from "@/hooks/use-immer-reducer.ts";
import useProjectsContext from "@/services/context/projects/use-projects-context.ts";
import FlowMode from "@/models/enums/flow-mode.ts";
import ProjectCreationReducerAction from "./project-creation-reducer-action.ts";
import ProjectCreationReducer from "./project-creation-reducer.ts";
import ProjectCreationModel from "../models/project-creation-model.ts";
import detectOrganisation from "../helpers/index.ts";
import {postItem} from "@/queries/queries-helper.ts";
import ProjectOrganisation from "@/pages/takeoff/context/project-organisation/models/project-organisation.ts";
import {BaamerFileRevisionDto} from "@/models/interfaces/dtos/baamer-file-revision-dto.ts";
import {PROJECT_REFERENCE_ENDPOINTS} from "@/services/endpoints.ts";

const ProjectCreationContextProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    
    const [selectedFlowCreationMode, setFlowModeCreationProject] = useState<FlowMode | undefined>(undefined);
    
    const [data, dispatch] = UseImmerReducer<ProjectCreationModel, ProjectCreationReducerAction>(ProjectCreationReducer, new ProjectCreationModel());
    const navigate = useNavigate();
    
    const filesRef = useRef<File[]>([]);
    const storeyHeightRef = useRef<number>(2.7);

    const { createProjectMutation, refetchProjectIds } = useProjectsContext();
    
    const [isCreatingProject, setIsCreatingProject] = useState<boolean>(false);
    const [creatingProjectPhase, setCreatingProjectPhase] = useState<string | React.ReactNode>("");
    
    const setProjectInformations = useCallback((name: string, localisationId: number) => {
        dispatch({
            type: "setInformations",
            payload: {
                name,
                localisationId
            }
        })
        
        const clonedData = structuredClone(data);
        clonedData.name = name;
        clonedData.localisationId = localisationId;
        return clonedData;
    }, [data, dispatch])
    
    const analyseFileNames = useCallback((files: File[], storeyHeight: number, forceSingleBuilding = false) => {
            filesRef.current = files;
            
            const detectedBuildings = detectOrganisation(files, storeyHeight, forceSingleBuilding);
            dispatch({
                type: "populateBuildings",
                payload: detectedBuildings,
            });
            
            const clonedData = structuredClone(data);
            clonedData.buildings = detectedBuildings;
            return clonedData;
    }, [data, dispatch]);
    
    const handleCreateOrganisation = useCallback(async (projectId: string, creationModel: ProjectCreationModel) => {
        const form = new FormData();
        form.append('Project', JSON.stringify(creationModel));
        filesRef.current.forEach((file: File) => {
            form.append('Files', file);
        });
        
        await postItem<{ projectReference: ProjectOrganisation, baamerFileRevisions: BaamerFileRevisionDto[] }>(
            PROJECT_REFERENCE_ENDPOINTS.SAVE(projectId),
            form
        );

        await refetchProjectIds(projectId);
    }, [refetchProjectIds]);

    const createProject = useCallback(async (creationModelOverride?: ProjectCreationModel) => {
        const creationModel = creationModelOverride ?? data;

        if(selectedFlowCreationMode === undefined || creationModel.localisationId === -1 || creationModel.name === '') return;
        
        setIsCreatingProject(true);
        setCreatingProjectPhase('Création du projet');
        
        try {
            const createdProject = await createProjectMutation.mutateAsync({
                name: creationModel.name,
                localisationId: creationModel.localisationId,
                flowMode: selectedFlowCreationMode
            });
            
            if(createdProject?.id === undefined) return;
            
            switch (selectedFlowCreationMode) {
                case FlowMode._2D3DAdvanced: {
                    setCreatingProjectPhase('Création des bâtiments et étages');
                    await handleCreateOrganisation(createdProject.id, creationModel);
                    setCreatingProjectPhase('Redirection vers le projet');
                    navigate(`/takeoff/${createdProject.id}?tab=organisation&showWalkthrough=true`);
                    break;
                }
                case FlowMode._2D3D: {
                    await handleCreateOrganisation(createdProject.id, creationModel);
                    setCreatingProjectPhase('Redirection vers le projet');
                    navigate(`/takeoff/${createdProject.id}`);
                    break;
                }
                case FlowMode._3D2D: {
                    setCreatingProjectPhase(
                        <div className='flex flex-col gap-y-2 text-center'> 
                            <p>Le projet {createdProject.name} a été créé.</p>
                            <p>Vous pouvez maintenant l'ouvrir dans <a className='text-blue-500 hover:text-blue-600 transition-colors' href='https://webservice.treegram.fr/treegram/getFile?link=TreegramSetupOffline.exe' target='_blank'>Treegram</a>.</p>
                        </div>
                    )
                    break;
                }
                default: {
                    break;
                }
            }
        } catch (e) {
            console.error(e);
        }
  
        setIsCreatingProject(false);
    }, [createProjectMutation, data, handleCreateOrganisation, navigate, selectedFlowCreationMode]);

    const contextProps: ProjectCreationContextProps = {
        flowModeCreationProject: selectedFlowCreationMode,
        setFlowModeCreationProject: setFlowModeCreationProject,
        
        data,

        setProjectInformations,

        currentFiles: filesRef.current,
        storeyHeight: storeyHeightRef.current,
        analyseFileNames,

        createProject,
        isCreatingProject,
        creatingProjectPhase,
    } 

    return (
        <ProjectCreationContext.Provider value={contextProps}>
            {children}
        </ProjectCreationContext.Provider>
    )
}

export default ProjectCreationContextProvider;